import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Key names, not copy: resolved through t() at render so a language switch
// re-reads them instead of freezing the English at module load.
const POINTS = [
  {
    l: "philosophy.handpicked",
    d: "philosophy.handpickedDesc",
    icon: (
      <path d="M12 3l2 5 5 .5-4 3.5 1.5 5L12 19l-4.5 3 1.5-5-4-3.5 5-.5z" />
    ),
  },
  {
    l: "philosophy.oneOfAKind",
    d: "philosophy.oneOfAKindDesc",
    icon: (
      <>
        <path d="M20.6 13.4L13.4 20.6a2 2 0 0 1-2.8 0l-6.2-6.2a2 2 0 0 1-.6-1.4V5a1 1 0 0 1 1-1h7a2 2 0 0 1 1.4.6l6.2 6.2a2 2 0 0 1 0 2.6z" />
        <circle cx="8" cy="8" r="1.4" />
      </>
    ),
  },
  {
    l: "philosophy.realBrand",
    d: "philosophy.realBrandDesc",
    icon: (
      <>
        <path d="M12 3l8 3v5c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" />
        <path d="M9 12l2 2 4-4" />
      </>
    ),
  },
];

/**
 * PhilosophyBand
 *
 * "Not a marketplace. A handpicked collection." band with three value points,
 * an authenticity-testing callout, and a "Shop verified handloom" CTA.
 */
export default function PhilosophyBand() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <section className="mx-4 mt-[6px] mb-[18px] p-4 bg-surface-peach rounded-lg border border-coral/[0.18]">
      <h2 className="text-[16px] font-[500] text-ink-900 tracking-[-0.01em] mb-[4px]">
        <Trans
          i18nKey="philosophy.heading"
          components={[<span className="text-coral-ink" />]}
        />
      </h2>
      <p className="text-[11.5px] text-ink-500 leading-[1.5] mb-[13px]">
        {t("philosophy.subheading")}
      </p>

      <div className="grid grid-cols-3 gap-[10px]">
        {POINTS.map((pt) => (
          <div key={pt.l} className="text-center">
            <div
              className="w-[36px] h-[36px] mx-auto mb-[7px] rounded-[10px] bg-white grid place-items-center text-coral-ink"
              style={{ boxShadow: "0 4px 18px rgba(31,33,46,.08)" }}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-[18px] h-[18px]"
              >
                {pt.icon}
              </svg>
            </div>
            <div className="text-[11px] font-[500] text-ink-900">{t(pt.l)}</div>
            <div className="text-[9.5px] text-ink-500 leading-[1.35] mt-[3px]">
              {t(pt.d)}
            </div>
          </div>
        ))}
      </div>

      {/* Authenticity-testing callout */}
      <div className="flex gap-[10px] items-start mt-[14px] px-[12px] py-[11px] bg-white border border-coral/[0.22] rounded-md">
        <span className="w-[30px] h-[30px] rounded-[8px] flex-none bg-surface-peach text-coral-ink grid place-items-center">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-[17px] h-[17px]"
          >
            <path d="M9 3h6" />
            <path d="M10 3v5.6L4.6 18A2 2 0 0 0 6.4 21h11.2a2 2 0 0 0 1.8-3L14 8.6V3" />
            <path d="M7.5 14h9" />
          </svg>
        </span>
        <div>
          <div className="text-[12px] font-[500] text-ink-900">
            {t("philosophy.testedTitle")}
          </div>
          <div className="text-[10px] text-ink-500 leading-[1.42] mt-[2px]">
            {t("philosophy.testedDesc")}
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => navigate("/verified-handloom")}
        className="flex w-fit items-center justify-center gap-[7px] mx-auto mt-4 mb-[2px] px-[26px] py-[13px] bg-coral-ink text-white text-[12.5px] font-[500] rounded-pill"
      >
        {t("philosophy.shopVerified")}
      </button>
    </section>
  );
}
